import { Component, inject } from '@angular/core';
import { TransferInService } from './transfer-in.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TransferOutService } from '../transfer-out-list/transfer-out.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { PaginationComponent } from '../Common/pagination/pagination.component';

type InboundTab = 'to_receive' | 'in_progress' | 'received';

@Component({
  selector: 'app-transfer-in-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PaginationComponent],
  templateUrl: './transfer-in-list.component.html',
  styleUrl: './transfer-in-list.component.scss',
})
export class TransferInListComponent {
  private transferInService = inject(TransferInService);
  private transferOutService = inject(TransferOutService);
  private confirmDialog = inject(ConfirmDialogService);

  transferLoading = false;
  currentPage = 1;
  pageSize = 10;
  pageCount = 1;
  searchTerm = '';

  activeTab: InboundTab = 'to_receive';

  // To Receive — TOs incoming + IN_TRANSIT, no TI started yet
  toReceiveList: any[] = [];
  // In Progress — TIs in DRAFT
  inProgressList: any[] = [];
  // Received — TIs in POSTED (clean or with discrepancy)
  receivedList: any[] = [];

  showOnlyDiscrepancy = false;

  ngOnInit(): void {
    this.loadTransfers();
  }

  setTab(tab: InboundTab) {
    this.activeTab = tab;
    this.currentPage = 1;
    this.searchTerm = '';
    this.showOnlyDiscrepancy = false;
    this.loadTransfers();
  }

  get currentList(): any[] {
    if (this.activeTab === 'to_receive') return this.toReceiveList;
    if (this.activeTab === 'in_progress') return this.inProgressList;
    if (this.showOnlyDiscrepancy) {
      return this.receivedList.filter((r) => r.partiallyReceived);
    }
    return this.receivedList;
  }

  loadTransfers() {
    this.transferLoading = true;

    if (this.activeTab === 'to_receive') {
      this.transferOutService
        .getTransferOuts(
          { page: this.currentPage, limit: this.pageSize },
          this.searchTerm,
          { direction: 'incoming', status: 'IN_TRANSIT' }
        )
        .subscribe((res: any) => {
          this.toReceiveList = res.data ?? [];
          this.pageCount = res.pageCount ?? 1;
          this.transferLoading = false;
        });
      return;
    }

    const status = this.activeTab === 'in_progress' ? 'DRAFT' : 'POSTED';
    this.transferInService
      .getTransferIns(
        { page: this.currentPage, limit: this.pageSize },
        this.searchTerm,
        { status }
      )
      .subscribe((res: any) => {
        if (this.activeTab === 'in_progress') {
          this.inProgressList = res.data ?? [];
        } else {
          this.receivedList = res.data ?? [];
        }
        this.pageCount = res.pageCount ?? 1;
        this.transferLoading = false;
      });
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.pageCount) {
      this.currentPage = page;
      this.loadTransfers();
    }
  }

  onPageSizeChange(limit: number) {
    this.pageSize = limit;
    this.pageSizeChange();
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadTransfers();
  }

  onSearch() {
    this.currentPage = 1;
    this.loadTransfers();
  }

  async finalizeTransfer(id: string) {
    const proceed = await this.confirmDialog.confirm({
      title: 'Finalize Transfer In?',
      message:
        'Are you sure you want to post this receipt? Stock will be updated at the destination and this cannot be reversed.',
      confirmText: 'Finalize',
      cancelText: 'Cancel',
    });
    if (!proceed) return;

    this.transferInService
      .finalizeTransferIn(id)
      .subscribe(() => this.loadTransfers());
  }

  toggleDiscrepancyFilter() {
    this.showOnlyDiscrepancy = !this.showOnlyDiscrepancy;
  }
}
